import { useMemo } from 'react';
import { type CSSObject } from '@emotion/react';
import { getModalBodyScrollStyles } from './getModalBodyScrollStyles';
import { useScrolling } from '../../../../hooks';
import { useTheme } from '../../../../theme';
import { SectionedScrollBehavior } from './modalBodySectionedScroll';

type FullScrollStyles = Record<'body', CSSObject>;

type SectionedScrollStyles = Record<
  'body' | 'fixedSection' | 'scrollableSection',
  CSSObject
>;

export const useModalBodyFullScrollStyles = (
  bodyElement: HTMLDivElement | null,
  withFooter: boolean,
): FullScrollStyles => {
  const theme = useTheme();
  const scrollPosition = useScrolling(bodyElement);

  const scrollStyles = useMemo(
    () => getModalBodyScrollStyles(bodyElement, scrollPosition),
    [bodyElement, scrollPosition],
  );

  const paddingBottom = withFooter ? 2 : 6;

  return {
    body: {
      flexGrow: 1,
      width: '100%',
      padding: theme.spacing(0, 6, paddingBottom),
      overflow: 'auto',
      ...scrollStyles,
    },
  };
};

export const useModalBodySectionedScrollStyles = (
  scrollBehavior: SectionedScrollBehavior,
): SectionedScrollStyles => {
  const theme = useTheme();

  return {
    body: {
      flexGrow: 1,
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      width: '100%',
      height: '100%',
      overflow: scrollBehavior === 'outer' ? 'hidden scroll' : 'hidden',
    },
    fixedSection: {
      width: '100%',
      padding:
        scrollBehavior === 'outer'
          ? theme.spacing(0, 4, 6, 6)
          : theme.spacing(0, 6, 6, 6),
    },
    scrollableSection: {
      width: '100%',
      padding: theme.spacing(0, 6, 6, 6),
      ...(scrollBehavior === 'inner' && {
        overflowY: 'auto',
      }),
    },
  };
};
